import { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import axiosClient from '@/services/axiosClient';

interface MeResponse {
  email: string;
  displayName: string;
  role: number;
  roles?: number[];
  capabilities?: string[];
  userId: string;
}

function isTokenValid(token: string): boolean {
  try {
    const padded = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(padded));
    return typeof payload.exp === 'number' && payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

function redirectToGoogle(): void {
  const googleAuthUrl = import.meta.env.VITE_GOOGLE_AUTH_URL;
  if (!googleAuthUrl) {
    throw new Error('VITE_GOOGLE_AUTH_URL must be configured.');
  }

  const state = crypto.randomUUID();
  sessionStorage.setItem('oauth_state', state);
  const params = new URLSearchParams({
    client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
    redirect_uri: `${window.location.origin}/login`,
    response_type: 'code',
    scope: 'openid email profile',
    hd: 'cre8orglobal.com',
    state,
    access_type: 'online',
    prompt: 'select_account',
  });
  window.location.href = `${googleAuthUrl}?${params}`;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const returnedState = params.get('state');
    const oauthError = params.get('error');

    if (oauthError) {
      setError('Google Sign-In was cancelled or failed. Please try again.');
      window.history.replaceState({}, '', '/login');
      return;
    }

    if (!code) return;

    const storedState = sessionStorage.getItem('oauth_state');
    if (!storedState || storedState !== returnedState) {
      setError('Security check failed. Please try signing in again.');
      window.history.replaceState({}, '', '/login');
      return;
    }

    sessionStorage.removeItem('oauth_state');
    window.history.replaceState({}, '', '/login');

    const redirectUri = `${window.location.origin}/login`;

    setLoading(true);
    setError(null);

    axiosClient
      .post<{ idToken: string }>('/auth/google/exchange', { code, redirectUri })
      .then(({ data: { idToken } }) =>
        axiosClient
          .get<MeResponse>('/auth/me', {
            headers: { Authorization: `Bearer ${idToken}` },
          })
          .then(({ data }) => {
            // Store atomically before navigating so ProtectedRoute and any component
            // reading cre8or_auth_user both see consistent state.
            sessionStorage.setItem('id_token', idToken);
            sessionStorage.setItem(
              'cre8or_auth_user',
              JSON.stringify({
                email: data.email,
                displayName: data.displayName,
                role: data.role,
                roles: data.roles?.length ? data.roles : [data.role],
                capabilities: data.capabilities ?? [],
                userId: data.userId,
              })
            );
            navigate('/leads', { replace: true });
          })
      )
      .catch(() => {
        setError('Sign-in failed. Make sure your @cre8orglobal.com account has been provisioned.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [navigate]);

  // Already authenticated — skip login page
  const existingToken = sessionStorage.getItem('id_token');
  if (existingToken && isTokenValid(existingToken)) {
    return <Navigate to="/leads" replace />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-sm rounded-2xl bg-white p-8 shadow-md">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900">Cre8or CRM</h1>
          <p className="mt-2 text-sm text-gray-500">Sign in with your Cre8or account</p>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <p className="py-2 text-center text-sm text-gray-500">Signing in…</p>
        ) : (
          <div className="flex justify-center">
            <button
              onClick={redirectToGoogle}
              className="flex items-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <GoogleSvgIcon />
              Sign in with Google
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function GoogleSvgIcon() {
  return (
    <svg aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 18 18">
      <path fill="#4285F4" d="M17.64 9.2a10.34 10.34 0 0 0-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92a8.78 8.78 0 0 0 2.68-6.62z" />
      <path fill="#34A853" d="M9 18a8.6 8.6 0 0 0 5.96-2.18l-2.92-2.26a5.43 5.43 0 0 1-8.09-2.85H.96v2.34A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.96 10.71a5.41 5.41 0 0 1 0-3.42V4.95H.96a9 9 0 0 0 0 8.1l3-2.34z" />
      <path fill="#EA4335" d="M9 3.58a4.86 4.86 0 0 1 3.44 1.35l2.58-2.59A8.65 8.65 0 0 0 9 0 9 9 0 0 0 .96 4.95l3 2.34A5.36 5.36 0 0 1 9 3.58z" />
    </svg>
  );
}
